import React, { useRef } from 'react';
import { Modal } from 'react-bootstrap';
import { Upload, ClipboardPaste } from 'lucide-react';
import { useDragDrop } from '../hooks/useDragDrop';
import { usePasteUpload } from '../hooks/usePasteUpload';

interface UploadPickerModalProps {
    show: boolean;
    onHide: () => void;
    /** Hand-off to the caller's existing upload flow. Receives the chosen files. */
    onFilesSelected: (files: File[]) => void;
    /** Forwarded to the file input (e.g. "image/*", ".pdf,image/*"). */
    accept?: string;
    /** Allow selecting multiple files. Defaults to true. */
    multiple?: boolean;
    title?: string;
}

/**
 * Shared "Ladda upp" entry point. Lets the user provide files three ways:
 * drag-and-drop onto the modal, paste (Ctrl/Cmd + V), or pick from the device.
 *
 * It only *collects* files — the caller keeps its own post-selection flow
 * (name prompt, per-image date editing, direct upload) via {@link onFilesSelected}.
 */
export const UploadPickerModal: React.FC<UploadPickerModalProps> = ({
    show,
    onHide,
    onFilesSelected,
    accept,
    multiple = true,
    title = 'Ladda upp',
}) => {
    const inputRef = useRef<HTMLInputElement>(null);

    const handleFiles = (files: File[]) => {
        if (files.length === 0) return;
        onFilesSelected(files);
        onHide();
    };

    const { isDragOver, dragHandlers } = useDragDrop({ onFilesDropped: handleFiles });
    usePasteUpload({ enabled: show, onFilesPasted: handleFiles });

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files || []);
        e.target.value = ''; // allow re-picking the same file
        handleFiles(files);
    };

    return (
        <Modal show={show} onHide={onHide} centered>
            <Modal.Header closeButton>
                <Modal.Title>{title}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div
                    {...dragHandlers}
                    onClick={() => inputRef.current?.click()}
                    style={{
                        border: isDragOver ? '2px dashed #007bff' : '2px dashed #ced4da',
                        borderRadius: '10px',
                        backgroundColor: isDragOver ? '#f0f8ff' : '#f8f9fa',
                        padding: '32px 24px',
                        textAlign: 'center',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                    }}
                >
                    <Upload
                        size={40}
                        style={{ color: isDragOver ? '#007bff' : '#6c757d', marginBottom: '12px' }}
                    />
                    <div style={{ fontWeight: 500, fontSize: '16px', marginBottom: '4px' }}>
                        Dra och släpp {multiple ? 'filer' : 'en fil'} här
                    </div>
                    <div
                        style={{
                            color: '#6c757d',
                            fontSize: '14px',
                            marginBottom: '16px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '6px',
                        }}
                    >
                        <ClipboardPaste size={15} />
                        eller klistra in med Ctrl + V
                    </div>
                    <button
                        type="button"
                        className="btn btn-primary"
                        onClick={(e) => {
                            e.stopPropagation();
                            inputRef.current?.click();
                        }}
                    >
                        <Upload size={15} className="me-2" />
                        Välj fil från enheten
                    </button>
                    <input
                        ref={inputRef}
                        type="file"
                        className="d-none"
                        accept={accept}
                        multiple={multiple}
                        onChange={handleInputChange}
                    />
                </div>
            </Modal.Body>
        </Modal>
    );
};
